import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useLocation } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OwnerSelect } from "@/components/agendamentos/OwnerSelect";
import { PropertySelect } from "@/components/agendamentos/PropertySelect";
import { IntervalSelect } from "@/components/agendamentos/IntervalSelect";
import { DateTimeSelect } from "@/components/agendamentos/DateTimeSelect";
import { MessageInput } from "@/components/agendamentos/MessageInput";
import { CampaignMenu } from "@/components/agendamentos/CampaignMenu";
import { Checkbox } from "@/components/ui/checkbox";

// Mock data for owners
const mockOwners = [
  {
    id: "1",
    name: "João Silva",
    email: "joao@email.com",
    phone: "(11) 99999-9999",
    whatsapp: "5511999999999",
    properties: [],
    registrationDate: new Date(),
    active: true
  },
  {
    id: "2",
    name: "Maria Santos",
    email: "maria@email.com",
    phone: "(11) 88888-8888",
    whatsapp: "5511888888888",
    properties: [],
    registrationDate: new Date(),
    active: true
  },
];

// Mock data for properties
const mockProperties = [
  {
    id: "1",
    title: "Apartamento Luxo",
    link: "https://example.com/apt1",
    price: 500000,
    ownerId: "1"
  },
  {
    id: "2",
    title: "Casa com Piscina",
    link: "https://example.com/house1",
    price: 850000,
    ownerId: "2"
  }
];

const SchedulingPage = () => {
  const { toast } = useToast();
  const location = useLocation();
  const { selectedOwner: initialOwner, selectedProperty: initialProperty } = location.state || {};
  
  const [activeTab, setActiveTab] = useState("individual");
  const [date, setDate] = useState<Date>();
  const [time, setTime] = useState("");
  const [selectedOwner, setSelectedOwner] = useState(initialOwner || "");
  const [selectedProperty, setSelectedProperty] = useState(initialProperty || "");
  const [messageInterval, setMessageInterval] = useState("30days");
  const [isIntervalEnabled, setIsIntervalEnabled] = useState(false);
  const [message, setMessage] = useState("");

  const handleTemplateSelect = (templateContent: string) => {
    if (!selectedOwner || !selectedProperty) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, selecione o proprietário e o imóvel antes de usar um template.",
        variant: "destructive",
      });
      return;
    }

    const owner = mockOwners.find(o => o.id === selectedOwner);
    const property = mockProperties.find(p => p.id === selectedProperty);
    
    if (!owner || !property) return;

    let finalMessage = templateContent
      .replace("{proprietario}", owner.name)
      .replace("{imovel}", property.title)
      .replace("{link}", property.link)
      .replace("{valor}", new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(property.price));

    if (date) {
      finalMessage += `\n\nData agendada: ${format(date, "dd/MM/yyyy", { locale: ptBR })}`;
    }
    
    if (time) {
      finalMessage += ` às ${time}`;
    }
    
    setMessage(finalMessage);
  };

  const handleSchedule = () => {
    if (!date || !time || !selectedOwner || !selectedProperty || !message) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha todos os campos necessários.",
        variant: "destructive",
      });
      return;
    }

    const scheduleInfo = {
      date,
      time,
      interval: isIntervalEnabled ? messageInterval : null,
      owner: mockOwners.find(o => o.id === selectedOwner),
      property: mockProperties.find(p => p.id === selectedProperty),
      message
    };

    toast({
      title: "Mensagem agendada",
      description: `Mensagem será enviada em ${format(date, "dd/MM/yyyy", { locale: ptBR })} às ${time}${
        isIntervalEnabled ? ` com repetição a cada ${messageInterval}` : ""
      }`,
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-heading font-bold text-gray-900">Agendamentos</h1>
        <p className="mt-2 text-gray-600">Gerencie suas mensagens e campanhas</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="individual">Mensagem Individual</TabsTrigger>
          <TabsTrigger value="campaign">Campanha</TabsTrigger>
        </TabsList>
        
        <TabsContent value="individual">
          <div className="bg-white p-4 md:p-6 rounded-xl shadow-card space-y-6">
            <h2 className="text-xl font-medium">Novo Agendamento</h2>
            
            <div className="grid gap-6">
              <OwnerSelect
                owners={mockOwners}
                value={selectedOwner}
                onChange={setSelectedOwner}
              />

              {selectedOwner && (
                <PropertySelect
                  properties={mockProperties}
                  ownerId={selectedOwner}
                  value={selectedProperty}
                  onChange={setSelectedProperty}
                />
              )}

              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="enableInterval"
                    checked={isIntervalEnabled}
                    onCheckedChange={(checked) => setIsIntervalEnabled(checked as boolean)}
                  />
                  <label
                    htmlFor="enableInterval"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Ativar intervalo de repetição
                  </label>
                </div>
                
                {isIntervalEnabled && (
                  <IntervalSelect
                    value={messageInterval}
                    onChange={setMessageInterval}
                  />
                )}
              </div>

              <DateTimeSelect
                date={date}
                time={time}
                onDateChange={setDate}
                onTimeChange={setTime}
              />

              <MessageInput
                value={message}
                onChange={setMessage}
                onTemplateSelect={handleTemplateSelect}
              />

              <Button onClick={handleSchedule} className="w-full">
                Agendar Mensagem
              </Button>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="campaign">
          <CampaignMenu owners={mockOwners} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SchedulingPage;
